import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './app/App';
import { listenForInstallSignals } from '@/features/install/install-store';
import { recordVisit } from '@/features/install/install';
import { registerServiceWorker } from '@/features/install/service-worker';
import './app/globals.css';

/**
 * The home-screen plumbing, before the first render.
 *
 * `beforeinstallprompt` can fire before React has mounted anything, and an
 * event with no listener is gone - so the listener is installed here, not in
 * a component. The visit is counted here for the same reason it is counted
 * at all: once per page load, however many routes are visited after. The
 * worker registers on `load`, in production only; see service-worker.ts.
 */
listenForInstallSignals();
try {
  recordVisit(localStorage);
} catch {
  // Private mode, or storage disabled. No count means no install banner,
  // which is the right quiet failure.
}
registerServiceWorker();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A 401 means the session is gone and lib/api.ts has already redirected;
      // retrying just does it again. A 4xx generally will not succeed on a
      // second attempt either, so only 5xx and network errors are worth a retry.
      retry: (failureCount, error) => {
        const status = (error as { statusCode?: number }).statusCode;
        if (status && status < 500) return false;
        return failureCount < 2;
      },
      staleTime: 30_000,
      // Money changes when someone else records a payment, so a background
      // refetch on focus is genuinely useful here rather than noise.
      refetchOnWindowFocus: true,
    },
  },
});

const root = document.getElementById('root');
if (!root) throw new Error('#root is missing from index.html');

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <App />
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
